import { useEffect, useId, useRef, useState, type KeyboardEvent, type ReactNode } from 'react'
import { createPortal } from 'react-dom'

import { useI18n } from '@/shared/i18n/I18nProvider'
import { Icon } from '@/shared/ui/icons'
import { usePopoverDismissal } from '@/shared/ui/popoverDismissal'
import { usePopoverPlacement } from '@/shared/ui/popoverPlacement'

import {
  appearanceOptionValue,
  DENSITIES,
  MODES,
  SCALES,
  THEMES,
  type DensityId,
  type ModeId,
  type ScaleId,
  type ThemeId,
} from './appearance'
import { useAppearance } from './AppearanceProvider'
import { Segmented } from './Segmented'

/**
 * The appearance picker: four axes of one choice, in one panel.
 *
 * Each axis is its own group, and each group is native radios in a
 * `<fieldset>` with a `<legend>`. That is not decoration: a radio group is one
 * tab stop whose arrow keys move between its alternatives, its legend names it
 * to a screen reader, and `aria-checked` is the browser's rather than ours. The
 * theme group shows each theme's real canvas, surface and accent as a swatch.
 *
 * THE PANEL STAYS OPEN WHILE THE DIALS TURN. Text size and density are judged
 * by looking at the product, so a control that closed on each press would make
 * the comparison a sequence of eight clicks. Every change applies immediately —
 * the panel is standing on the product it is restyling — and the way out is
 * Escape, a click outside, or moving focus away.
 *
 * Placement is `placeDropPanel`, the one owner of the mirror, the clamp and
 * the flip: `position: fixed`, portaled to `<body>` so an ancestor's
 * `overflow: auto` cannot clip it, hung from the trigger's reading-END edge
 * (`align: 'end'`) because the trigger sits at the end of the header bar.
 */

const THEME_NAME_ID: Record<
  ThemeId,
  | 'appearance.themeTerys'
  | 'appearance.themeEmerald'
  | 'appearance.themeAmber'
  | 'appearance.themeGraphite'
  | 'appearance.themeSmartomica'
  | 'appearance.themeSmartomicaBrand'
> = {
  terys: 'appearance.themeTerys',
  emerald: 'appearance.themeEmerald',
  amber: 'appearance.themeAmber',
  graphite: 'appearance.themeGraphite',
  smartomica: 'appearance.themeSmartomica',
  smartomicabrand: 'appearance.themeSmartomicaBrand',
}

const MODE_NAME_ID: Record<ModeId, 'appearance.light' | 'appearance.dark'> = {
  light: 'appearance.light',
  dark: 'appearance.dark',
}

const DENSITY_NAME_ID: Record<
  DensityId,
  'appearance.densityCompact' | 'appearance.densityCozy' | 'appearance.densityRelaxed'
> = {
  compact: 'appearance.densityCompact',
  cozy: 'appearance.densityCozy',
  relaxed: 'appearance.densityRelaxed',
}

/**
 * The panel's width, in `rem` — the SAME NUMBER the stylesheet states
 * (`appearanceMenu.css`, `inline-size: min(17rem, …)`) and deliberately not a
 * pixel constant beside it.
 *
 * This panel contains the dial that changes what a `rem` is, so a width fixed
 * in pixels would be right at 100% and wrong at both ends of its own control:
 * at 110% the panel renders 27px wider than the placement believes and hangs
 * off the reading edge, which is worst exactly where someone asked for larger
 * text. Converting through the live root font size below keeps the two in step
 * however the dial is turned.
 */
const PANEL_REM = 17

export function AppearanceMenu() {
  const { t } = useI18n()
  const { appearance, setAppearance } = useAppearance()
  const group = useId()
  const triggerRef = useRef<HTMLButtonElement>(null)
  const panelRef = useRef<HTMLDivElement>(null)
  const focusOnOpen = useRef(false)
  const [open, setOpen] = useState(false)
  const stated = appearanceOptionValue(appearance)

  // The panel scrolls inside itself and states no height, so it takes the room
  // on the side it lands on — below by default, above when below is a sliver —
  // which is the difference between a panel that scrolls its last group into
  // reach and one whose last group is under the fold of a laptop screen with no
  // way to get at it.
  //
  // The scale dial changes what a `rem` is, so the panel it lives in changes
  // width the moment it is turned — and a panel placed for the old width sits
  // off its anchor until something else happens to reposition it. Hence
  // `placeAgainOn`.
  const position = usePopoverPlacement({
    open,
    anchor: triggerRef,
    request: (anchor) => ({ anchor, gap: 6, align: 'end', widthRem: PANEL_REM }),
    placeAgainOn: appearance.scale,
  })

  usePopoverDismissal({ open, inside: [triggerRef, panelRef], dismiss: () => close(false) })

  // The dialog takes focus ONCE per opening, so Escape reaches it and a screen
  // reader is told where it landed. Not a trap: this is a non-modal popover
  // over the product it restyles, and tabbing out of it closes it below. The
  // flag is what keeps a scroll-driven reposition from stealing focus back
  // from whichever dial the reader had moved to.
  useEffect(() => {
    if (open && position && focusOnOpen.current) {
      focusOnOpen.current = false
      panelRef.current?.focus()
    }
  }, [open, position])

  function close(refocus: boolean) {
    setOpen(false)
    if (refocus) {
      triggerRef.current?.focus()
    }
  }

  function onKeyDown(event: KeyboardEvent<HTMLDivElement>) {
    if (event.key === 'Escape') {
      event.preventDefault()
      event.stopPropagation()
      close(true)
    }
  }

  return (
    <>
      <button
        ref={triggerRef}
        type="button"
        className="appearance-trigger"
        aria-haspopup="dialog"
        aria-expanded={open}
        aria-label={t('appearance.label')}
        title={t('appearance.label')}
        // Which appearance the CLOSED control states, in a form a test can read
        // without interpreting a picture.
        data-appearance={stated}
        onClick={() => {
          if (open) {
            close(false)
          } else {
            focusOnOpen.current = true
            setOpen(true)
          }
        }}
      >
        {/* Decorative: the button carries the accessible name. A half-filled
            disc rather than a swatch of any theme, because the control must
            read as "appearance" in all of them. */}
        <svg className="appearance-trigger__glyph" viewBox="0 0 20 20" aria-hidden="true" focusable="false">
          <circle cx="10" cy="10" r="7.25" fill="none" stroke="currentColor" strokeWidth="1.5" />
          <path d="M10 2.75a7.25 7.25 0 0 1 0 14.5Z" fill="currentColor" />
        </svg>
      </button>
      {open && position
        ? createPortal(
            <div
              ref={panelRef}
              className="appearance-menu"
              role="dialog"
              aria-label={t('appearance.label')}
              tabIndex={-1}
              style={{ left: position.left, top: position.top, maxHeight: position.maxHeight }}
              onKeyDown={onKeyDown}
              onBlur={(event) => {
                const next = event.relatedTarget as Node | null
                if (next && !panelRef.current?.contains(next) && next !== triggerRef.current) {
                  close(false)
                }
              }}
            >
              <Group legend={t('appearance.theme')}>
                <div className="appearance-menu__themes">
                  {THEMES.map((theme) => (
                    <label key={theme} className="appearance-theme">
                      <input
                        className="visually-hidden appearance-theme__input"
                        type="radio"
                        name={`${group}-theme`}
                        checked={appearance.theme === theme}
                        onChange={() => setAppearance({ ...appearance, theme })}
                      />
                      {/* The theme's own canvas, surface and accent. The values
                          live in `appearanceMenu.css`, keyed by this attribute
                          and checked against the real tokens by
                          `web/tests/style-appearance.test.ts`. */}
                      <span
                        className="appearance-theme__swatch"
                        data-swatch={appearanceOptionValue({ ...appearance, theme })}
                        aria-hidden="true"
                      />
                      <span className="appearance-theme__name">{t(THEME_NAME_ID[theme])}</span>
                      <span className="appearance-theme__mark" aria-hidden="true">
                        <Icon name="check" size="sm" />
                      </span>
                    </label>
                  ))}
                </div>
              </Group>
              <Group legend={t('appearance.mode')}>
                  <Segmented
                    name={`${group}-mode`}
                    options={MODES.map((mode) => ({ id: mode, label: t(MODE_NAME_ID[mode]) }))}
                    value={appearance.mode}
                    onPick={(mode) => setAppearance({ ...appearance, mode })}
                  />
              </Group>
              <Group legend={t('appearance.scale')}>
                <Segmented
                  name={`${group}-scale`}
                  options={SCALES.map((scale) => ({
                    id: scale,
                    label: t('appearance.scaleValue', { percent: scale }),
                  }))}
                  value={appearance.scale}
                  onPick={(scale: ScaleId) => setAppearance({ ...appearance, scale })}
                />
              </Group>
              <Group legend={t('appearance.density')}>
                <Segmented
                  name={`${group}-density`}
                  options={DENSITIES.map((density) => ({
                    id: density,
                    label: t(DENSITY_NAME_ID[density]),
                  }))}
                  value={appearance.density}
                  onPick={(density: DensityId) => setAppearance({ ...appearance, density })}
                />
              </Group>
            </div>,
            document.body,
          )
        : null}
    </>
  )
}

/**
 * One axis. A real `<fieldset>`/`<legend>` rather than a heading and a `div`,
 * because that is what names a radio group to a screen reader without an
 * `aria-labelledby` to keep in step with it.
 */
function Group({ legend, children }: { legend: string; children: ReactNode }) {
  return (
    <fieldset className="appearance-menu__group">
      <legend className="appearance-menu__legend">{legend}</legend>
      {children}
    </fieldset>
  )
}
